/**
 * Epic-Ready CPT String Generator
 * 
 * Generates a clean, labeled billing code block for copy/paste into Epic.
 * Each CPT line includes modifiers, units, and authorization references.
 * 
 * HARD BOUNDARIES:
 * - LINK does NOT submit claims
 * - CPTs come exclusively from reconciled, surgeon-confirmed procedures
 * - Modifiers are not altered — they come from Phase 3 sequencing
 */

import type { ExportCptLine } from './exportPackageGenerator';

export interface CptStringInput {
  caseNumber: string;
  authReference?: string;
  dateOfService?: string;
  surgeonName?: string;
  laterality?: string;
  cptLines: ExportCptLine[];
  isDemoExport: boolean;
  /** Include inline reference tags for proof map / surgeon confirmation */
  includeReferenceTags?: boolean;
}

/**
 * Generate a clean CPT string block for Epic copy/paste.
 */
export function generateEpicCptString(input: CptStringInput): string {
  const lines: string[] = [];

  if (input.isDemoExport) {
    lines.push('╔════════════════════════════════════════════════════════════╗');
    lines.push('║   DEMO MODE — ILLUSTRATIVE ONLY — NO CLAIMS SUBMITTED    ║');
    lines.push('╚════════════════════════════════════════════════════════════╝');
    lines.push('');
  }

  lines.push('BILLING CODES (Per Operative Documentation)');
  lines.push('═'.repeat(56));
  lines.push('');

  // Auth reference
  if (input.authReference) {
    lines.push(`Authorization: ${input.authReference}`);
  }
  if (input.dateOfService) {
    lines.push(`Date of Service: ${input.dateOfService}`);
  }
  if (input.laterality) {
    lines.push(`Laterality: ${input.laterality}`);
  }
  lines.push('');

  // CPT table header
  lines.push('Seq  CPT Code     Modifiers      Units  Description');
  lines.push('───  ──────────   ────────────   ─────  ─────────────────────────────');

  for (const cpt of input.cptLines) {
    const seq = String(cpt.sequence).padEnd(5);
    const code = cpt.cptCode.padEnd(13);
    const mods = cpt.modifiers.length > 0 
      ? cpt.modifiers.join(', ').padEnd(15) 
      : '—'.padEnd(15);
    const units = String(cpt.units).padEnd(7);
    const desc = (cpt.cptDescription || '').substring(0, 40);
    lines.push(`${seq}${code}${mods}${units}${desc}`);
  }

  lines.push('');

  // Modifier explanations (always included for billing clarity)
  const hasExplanations = input.cptLines.some(
    l => Object.keys(l.modifierExplanations).length > 0
  );
  if (hasExplanations) {
    lines.push('MODIFIER RATIONALE:');
    lines.push('───────────────────');
    for (const cpt of input.cptLines) {
      for (const [mod, reason] of Object.entries(cpt.modifierExplanations)) {
        lines.push(`  ${cpt.cptCode}-${mod}: ${reason}`);
      }
    }
    lines.push('');
  }

  // Reference tags (not part of the paste block but shown for traceability)
  if (input.includeReferenceTags) {
    lines.push('REFERENCE TAGS (Internal — Not for Pasting):');
    lines.push('───────────────────────────────────────────');
    for (const cpt of input.cptLines) {
      lines.push(`  ${cpt.cptCode}: [Proof Map ✓] [Surgeon Confirmed ✓]`);
    }
    lines.push('');
  }

  // Footer
  lines.push('───────────────────────────────────────────');
  lines.push('Documentation & Revenue Integrity Support');
  lines.push('This output does not constitute a claim submission.');
  if (input.isDemoExport) {
    lines.push('*** DEMO DATA — NOT FOR CLINICAL OR BILLING USE ***');
  }

  return lines.join('\n');
}

/**
 * Generate a minimal CPT-only string (just codes and modifiers, for quick paste).
 */
export function generateMinimalCptString(cptLines: ExportCptLine[]): string {
  return cptLines.map(l => l.finalCodeString).join('\n');
}
